// frontend/src/contexts/ClientAuthContext.js
import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientLogin as apiLogin, clientLogout as apiLogout } from '../services/clientApi';
import axios from 'axios';

const ClientAuthContext = createContext(null);
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

export const useClientAuth = () => {
  const context = useContext(ClientAuthContext);
  if (!context) throw new Error('useClientAuth must be used within ClientAuthProvider');
  return context;
};

export const ClientAuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const isRefreshing = useRef(false);

  // ✅ Vérifier expiration
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch { return true; }
  };

  // ✅ Rafraîchir le token client
  const refreshAccessToken = async () => {
    if (isRefreshing.current) return false;
    const refreshToken = localStorage.getItem('client_refresh_token');
    if (!refreshToken) return false;

    isRefreshing.current = true;
    try {
      const response = await axios.post(`${API_BASE}/client/auth/refresh`, {
        refresh_token: refreshToken
      });
      const { access_token } = response.data;
      localStorage.setItem('client_access_token', access_token);
      return true;
    } catch {
      return false;
    } finally {
      isRefreshing.current = false;
    }
  };

  // ✅ Exposer une fonction pour obtenir un token valide
  const getValidToken = async () => {
    const token = localStorage.getItem('client_access_token');
    if (!isTokenExpired(token)) return token;

    const refreshed = await refreshAccessToken();
    if (refreshed) return localStorage.getItem('client_access_token');

    logout(); // token expiré et refresh échoué → déconnexion
    return null;
  };

  useEffect(() => {
    const token = localStorage.getItem('client_access_token');
    const savedClient = localStorage.getItem('client_data');

    if (token && savedClient) {
      try {
        const clientData = JSON.parse(savedClient);
        setClient(clientData);
        setPackages(clientData.packages || []);
      } catch {
        localStorage.removeItem('client_access_token');
        localStorage.removeItem('client_refresh_token');
        localStorage.removeItem('client_data');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await apiLogin(email, password);
    const { access_token, refresh_token, client: clientData } = response.data;

    localStorage.setItem('client_access_token', access_token);
    localStorage.setItem('client_refresh_token', refresh_token);
    localStorage.setItem('client_data', JSON.stringify(clientData));

    setClient(clientData);
    setPackages(clientData.packages || []);
    return clientData;
  };

  const logout = async () => {
    try { await apiLogout(); } catch {}
    
    // Supprimer l'historique IA de ce client
    if (client?.id) {
      localStorage.removeItem(`assistant_chat_history_${client.id}`);
    }
    
    localStorage.removeItem('client_access_token');
    localStorage.removeItem('client_refresh_token');
    localStorage.removeItem('client_data');
    setClient(null);
    setPackages([]);
    navigate('/');
  };

  const hasPackage = (packageName) =>
    packages.some(p => p.name === packageName || p === packageName);

  return (
    <ClientAuthContext.Provider value={{
      client, loading, packages, hasPackage,
      login, logout, getValidToken,   // ✅ exposé
      isAuthenticated: !!client
    }}>
      {children}
    </ClientAuthContext.Provider>
  );
};